import { useState, useEffect, useCallback } from "react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine
} from "recharts";
import {
  TrendingUp, TrendingDown, DollarSign, BarChart3,
  PieChart as PieIcon, Activity, ArrowUpRight, ArrowDownRight,
  RefreshCw, Camera, Target, Zap, CheckCircle, XCircle, Clock
} from "lucide-react";
import { performanceAPI, trackRecordAPI } from "../services/api";

const COLORS = [
  "#22c55e", "#3b82f6", "#eab308", "#ef4444", "#a855f7",
  "#06b6d4", "#f97316", "#ec4899", "#14b8a6", "#8b5cf6"
];

const formatUSD = (num) => {
  if (!num && num !== 0) return "--";
  return `$${num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-slate-300 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="font-mono font-bold" style={{ color: p.color }}>
          {formatUSD(p.value)}
        </div>
      ))}
    </div>
  );
};

export default function PerformanceTab({ portfolio, prices, getPrice, owner, cachedSnapshots, setCachedSnapshots }) {
  const [snapshots, setSnapshots] = useState(cachedSnapshots || []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [trackRecord, setTrackRecord] = useState({ signals: [], stats: {} });
  const [trackLoading, setTrackLoading] = useState(false);
  const [showTrackRecord, setShowTrackRecord] = useState(true);
  const [sourceFilter, setSourceFilter] = useState("ALL");

  const loadSnapshots = useCallback(async () => {
    setLoading(true);
    const data = await performanceAPI.getSnapshots(365);
    setSnapshots(data || []);
    if (setCachedSnapshots) setCachedSnapshots(data || []);
    setLoading(false);
  }, [setCachedSnapshots]);

  const loadTrackRecord = useCallback(async () => {
    setTrackLoading(true);
    const data = await trackRecordAPI.get(owner || "");
    setTrackRecord(data || { signals: [], stats: {} });
    setTrackLoading(false);
  }, [owner]);

  // Only fetch on first visit
  useEffect(() => { if (snapshots.length === 0) { loadSnapshots(); loadTrackRecord(); } }, []);

  // Save current snapshot
  const saveSnapshot = useCallback(async () => {
    if (!portfolio.length) return;
    setSaving(true);
    const allPrices = { ...prices };
    const totalValue = portfolio.reduce((s, p) => s + getPrice(p) * p.qty, 0);
    const totalCost = portfolio.reduce((s, p) => s + p.avg * p.qty, 0);
    await performanceAPI.saveSnapshot(portfolio, allPrices, totalValue, totalCost);
    await loadSnapshots();
    setSaving(false);
  }, [portfolio, prices, getPrice, loadSnapshots]);

  // Current portfolio metrics
  const totalValue = portfolio.reduce((s, p) => s + getPrice(p) * p.qty, 0);
  const totalCost = portfolio.reduce((s, p) => s + p.avg * p.qty, 0);
  const totalPnl = totalValue - totalCost;
  const totalPnlPct = totalCost ? ((totalPnl / totalCost) * 100) : 0;

  // Position P&L data for bar chart
  const positionPnl = portfolio
    .map(p => {
      const price = getPrice(p);
      const pnl = (price - p.avg) * p.qty;
      const pnlPct = ((price - p.avg) / p.avg) * 100;
      return { ticker: p.ticker, name: p.name, pnl: Math.round(pnl), pnlPct: +pnlPct.toFixed(1), value: Math.round(price * p.qty) };
    })
    .sort((a, b) => b.pnl - a.pnl);

  // Sector allocation
  const sectorMap = {};
  portfolio.forEach(p => {
    const s = p.sector || "Autre";
    sectorMap[s] = (sectorMap[s] || 0) + getPrice(p) * p.qty;
  });
  const sectorData = Object.entries(sectorMap)
    .map(([name, value]) => ({ name, value: Math.round(value), pct: +((value / totalValue) * 100).toFixed(1) }))
    .sort((a, b) => b.value - a.value);

  // Top/worst performers
  const sorted = [...positionPnl];
  const bestPerformer = sorted[0];
  const worstPerformer = sorted[sorted.length - 1];

  // Value chart data from snapshots
  const valueChartData = snapshots.map(s => ({
    date: s.date?.slice(5),
    value: s.total_value,
    pnl: s.total_pnl
  }));

  // Add current as "today" if we have data
  if (totalValue > 0) {
    const today = new Date().toISOString().slice(5, 10);
    const lastDate = valueChartData.length ? valueChartData[valueChartData.length - 1].date : "";
    if (lastDate !== today) {
      valueChartData.push({ date: today, value: Math.round(totalValue), pnl: Math.round(totalPnl) });
    }
  }

  return (
    <div className="animate-fade-in" data-testid="performance-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2 sm:gap-3">
            <Activity className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-400" />Performance
          </h1>
          <p className="text-slate-400 mt-1 text-xs sm:text-sm">Suivi et analyse de votre portefeuille</p>
        </div>
        <button onClick={saveSnapshot} disabled={saving || !portfolio.length}
          className="px-4 sm:px-6 py-2 sm:py-3 bg-emerald-500 text-white font-bold rounded-xl text-xs sm:text-sm flex items-center gap-2 shadow-glow hover:shadow-glow-lg disabled:opacity-40"
          data-testid="save-snapshot-btn">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          Sauvegarder
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 mb-4 sm:mb-6">
        {[
          { label: "Valeur totale", value: formatUSD(totalValue), icon: DollarSign, gradient: "gradient-green" },
          { label: "P&L Total", value: `${totalPnl >= 0 ? "+" : ""}${formatUSD(totalPnl)}`, sub: `${totalPnlPct >= 0 ? "+" : ""}${totalPnlPct.toFixed(1)}%`, icon: totalPnl >= 0 ? TrendingUp : TrendingDown, gradient: totalPnl >= 0 ? "gradient-green" : "gradient-red" },
          { label: "Meilleur", value: bestPerformer?.ticker || "--", sub: bestPerformer ? `+${formatUSD(bestPerformer.pnl)}` : "", icon: ArrowUpRight, gradient: "gradient-blue" },
          { label: "Pire", value: worstPerformer?.ticker || "--", sub: worstPerformer ? `${formatUSD(worstPerformer.pnl)}` : "", icon: ArrowDownRight, gradient: "gradient-red" },
          { label: "Snapshots", value: snapshots.length, sub: snapshots.length ? `depuis ${snapshots[0]?.date?.slice(5)}` : "Aucun", icon: BarChart3, gradient: "gradient-gold" }
        ].map((card, i) => (
          <div key={i} className={`${card.gradient} rounded-xl p-3 sm:p-4 relative overflow-hidden`} data-testid={`kpi-card-${i}`}>
            <card.icon className="absolute top-2 right-2 sm:top-3 sm:right-3 w-6 h-6 sm:w-7 sm:h-7 opacity-20" />
            <div className="text-[9px] sm:text-[10px] text-slate-300 font-medium uppercase tracking-wider mb-1">{card.label}</div>
            <div className="text-base sm:text-xl font-bold font-mono text-white">{card.value}</div>
            {card.sub && <div className="text-[10px] sm:text-xs text-slate-300 mt-0.5">{card.sub}</div>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
        {/* Value Over Time */}
        <div className="lg:col-span-8 glass-card rounded-xl p-3 sm:p-5">
          <h3 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />Valeur du portefeuille
          </h3>
          {valueChartData.length > 1 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={valueChartData}>
                <defs>
                  <linearGradient id="valueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="value" stroke="#22c55e" fill="url(#valueGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[280px] text-slate-500">
              <Activity className="w-12 h-12 mb-3" />
              <p className="text-sm">Sauvegardez des snapshots pour voir l'historique</p>
              <p className="text-xs text-slate-600 mt-1">Cliquez "Sauvegarder snapshot" pour commencer</p>
            </div>
          )}
        </div>

        {/* Sector Allocation */}
        <div className="lg:col-span-4 glass-card rounded-xl p-3 sm:p-5">
          <h3 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            <PieIcon className="w-5 h-5 text-emerald-400" />Allocation sectorielle
          </h3>
          {sectorData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={sectorData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" stroke="none">
                    {sectorData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {sectorData.slice(0, 6).map((s, i) => (
                  <div key={s.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-slate-300 truncate max-w-[120px]">{s.name}</span>
                    </div>
                    <span className="font-mono text-white">{s.pct}%</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[180px] text-slate-500 text-sm">Aucune position</div>
          )}
        </div>
      </div>

      {/* P&L by Position */}
      <div className="glass-card rounded-xl p-3 sm:p-5 mt-4 sm:mt-6">
        <h3 className="font-display text-base sm:text-lg font-bold mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-emerald-400" />P&L par position
        </h3>
        {positionPnl.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(250, positionPnl.length * 36)}>
            <BarChart data={positionPnl} layout="vertical" margin={{ left: 50, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
              <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="ticker" tick={{ fill: "#cbd5e1", fontSize: 12, fontFamily: "monospace" }} tickLine={false} axisLine={false} width={55} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-xs shadow-xl">
                    <div className="font-bold text-white mb-1">{d.name} ({d.ticker})</div>
                    <div className={`font-mono ${d.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {d.pnl >= 0 ? "+" : ""}{formatUSD(d.pnl)} ({d.pnlPct >= 0 ? "+" : ""}{d.pnlPct}%)
                    </div>
                    <div className="text-slate-400 mt-1">Valeur: {formatUSD(d.value)}</div>
                  </div>
                );
              }} />
              <Bar dataKey="pnl" radius={[0, 4, 4, 0]}>
                {positionPnl.map((entry, i) => (
                  <Cell key={i} fill={entry.pnl >= 0 ? "#22c55e" : "#ef4444"} fillOpacity={0.8} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-slate-500 text-sm">Aucune position</div>
        )}
      </div>

      {/* Position Detail Table */}
      <div className="glass-card rounded-xl overflow-hidden mt-4 sm:mt-6">
        <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-slate-700">
          <h3 className="font-display text-base sm:text-lg font-bold">Détail des positions</h3>
        </div>
        <div className="hidden sm:grid grid-cols-8 px-5 py-3 bg-slate-800/50 border-b border-slate-700 table-header">
          <div className="col-span-2">Asset</div>
          <div className="text-right">Prix</div>
          <div className="text-right">Coût</div>
          <div className="text-right">Valeur</div>
          <div className="text-right">P&L ($)</div>
          <div className="text-right">P&L (%)</div>
          <div className="text-right">% Portfolio</div>
        </div>
        {positionPnl.map((pos, i) => {
          const weight = totalValue ? ((pos.value / totalValue) * 100).toFixed(1) : 0;
          return (
            <div key={i} data-testid={`perf-row-${pos.ticker}`}>
              {/* Desktop row */}
              <div className={`hidden sm:grid grid-cols-8 px-5 py-3 items-center ${i < positionPnl.length - 1 ? "border-b border-slate-700/50" : ""}`}>
                <div className="col-span-2">
                  <div className="font-semibold text-sm text-white">{pos.name}</div>
                  <div className="text-xs text-slate-400 font-mono">{pos.ticker}</div>
                </div>
                <div className="text-right font-mono text-sm text-slate-200">{formatUSD(getPrice(portfolio.find(p => p.ticker === pos.ticker)))}</div>
                <div className="text-right font-mono text-sm text-slate-400">{formatUSD(pos.value - pos.pnl)}</div>
                <div className="text-right font-mono text-sm text-white font-semibold">{formatUSD(pos.value)}</div>
                <div className={`text-right font-mono text-sm font-bold ${pos.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {pos.pnl >= 0 ? "+" : ""}{formatUSD(pos.pnl)}
                </div>
                <div className={`text-right font-mono text-sm font-bold ${pos.pnlPct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {pos.pnlPct >= 0 ? "+" : ""}{pos.pnlPct}%
                </div>
                <div className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(weight, 100)}%` }} />
                    </div>
                    <span className="font-mono text-xs text-slate-300">{weight}%</span>
                  </div>
                </div>
              </div>
              {/* Mobile card */}
              <div className={`sm:hidden px-3 py-2.5 ${i < positionPnl.length - 1 ? "border-b border-slate-700/50" : ""}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-bold text-sm text-white">{pos.ticker}</div>
                    <div className="text-[10px] text-slate-500">{pos.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm text-white">{formatUSD(pos.value)}</div>
                    <div className={`font-mono text-xs font-bold ${pos.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {pos.pnl >= 0 ? "+" : ""}{formatUSD(pos.pnl)} ({pos.pnlPct >= 0 ? "+" : ""}{pos.pnlPct}%)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ==================== AI SIGNAL TRACK RECORD ==================== */}
      <div className="glass-card rounded-xl overflow-hidden mt-6" data-testid="track-record-section">
        <div className="px-5 py-4 border-b border-slate-700 flex justify-between items-center">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Target className="w-5 h-5 text-amber-400" />Track Record AI
          </h3>
          <div className="flex items-center gap-3">
            {/* Source Filter */}
            <div className="flex items-center bg-slate-800 rounded-lg p-0.5" data-testid="source-filter">
              {["ALL", "ALGO", "EARNINGS"].map(f => (
                <button key={f} onClick={() => setSourceFilter(f)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${sourceFilter === f ? 'bg-amber-500/20 text-amber-400 shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
                  data-testid={`filter-${f.toLowerCase()}`}>
                  {f === "ALL" ? "Tous" : f}
                </button>
              ))}
            </div>
            <button onClick={loadTrackRecord} disabled={trackLoading}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs flex items-center gap-1.5 transition-colors"
              data-testid="refresh-track-record-btn">
              <RefreshCw className={`w-3 h-3 ${trackLoading ? 'animate-spin' : ''}`} />Rafraîchir
            </button>
            <button onClick={() => setShowTrackRecord(!showTrackRecord)}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs transition-colors"
              data-testid="toggle-track-record-btn">
              {showTrackRecord ? 'Masquer' : 'Afficher'}
            </button>
          </div>
        </div>

        {showTrackRecord && (() => {
          const filtered = sourceFilter === "ALL"
            ? trackRecord.signals || []
            : (trackRecord.signals || []).filter(s => s.signal_type?.includes(sourceFilter));
          const fWins = filtered.filter(s => s.status === "WIN").length;
          const fLosses = filtered.filter(s => s.status === "LOSS").length;
          const fOpen = filtered.filter(s => s.status === "OPEN").length;
          const fPnls = filtered.filter(s => s.pnl_pct != null).map(s => s.pnl_pct);
          const fStats = {
            total: filtered.length,
            wins: fWins,
            losses: fLosses,
            open: fOpen,
            win_rate: (fWins + fLosses) > 0 ? +((fWins / (fWins + fLosses)) * 100).toFixed(1) : 0,
            avg_pnl: fPnls.length > 0 ? +(fPnls.reduce((a, b) => a + b, 0) / fPnls.length).toFixed(2) : 0,
          };
          return (
          <>
            {/* Stats Summary */}
            {fStats.total > 0 && (
              <div className="grid grid-cols-6 gap-3 p-5 border-b border-slate-700" data-testid="track-record-stats">
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Total</div>
                  <div className="text-xl font-bold font-mono text-white">{fStats.total}</div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Wins</div>
                  <div className="text-xl font-bold font-mono text-emerald-400">{fStats.wins}</div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Losses</div>
                  <div className="text-xl font-bold font-mono text-red-400">{fStats.losses}</div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Open</div>
                  <div className="text-xl font-bold font-mono text-blue-400">{fStats.open}</div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Win Rate</div>
                  <div className={`text-xl font-bold font-mono ${fStats.win_rate >= 50 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {fStats.win_rate}%
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">P&L Moyen</div>
                  <div className={`text-xl font-bold font-mono ${fStats.avg_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {fStats.avg_pnl >= 0 ? '+' : ''}{fStats.avg_pnl}%
                  </div>
                </div>
              </div>
            )}

            {/* Cumulative P&L Chart */}
            {filtered.length >= 2 && (
              <div className="p-5 border-b border-slate-700" data-testid="track-record-chart">
                <h4 className="text-sm font-bold text-slate-300 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-amber-400" />P&L Cumulé des Signaux AI
                </h4>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={(() => {
                    const sorted = [...filtered]
                      .filter(s => s.pnl_pct !== null && s.pnl_pct !== undefined)
                      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                    let cumulative = 0;
                    return sorted.map((s, i) => {
                      cumulative += s.pnl_pct;
                      return {
                        label: s.ticker,
                        date: new Date(s.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
                        pnl: +s.pnl_pct.toFixed(2),
                        cumulative: +cumulative.toFixed(2),
                        idx: i + 1,
                      };
                    });
                  })()}>
                    <defs>
                      <linearGradient id="cumulGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={v => `${v}%`} />
                    <ReferenceLine y={0} stroke="#475569" strokeDasharray="3 3" />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-xs shadow-xl">
                          <div className="font-bold text-white mb-1">{d.label} <span className="text-slate-400">({d.date})</span></div>
                          <div className={`font-mono ${d.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            Signal: {d.pnl >= 0 ? '+' : ''}{d.pnl}%
                          </div>
                          <div className={`font-mono font-bold ${d.cumulative >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                            Cumulé: {d.cumulative >= 0 ? '+' : ''}{d.cumulative}%
                          </div>
                        </div>
                      );
                    }} />
                    <Area type="monotone" dataKey="cumulative" stroke="#f59e0b" fill="url(#cumulGrad)" strokeWidth={2.5} dot={{ fill: '#f59e0b', r: 4, strokeWidth: 2, stroke: '#1e293b' }} activeDot={{ r: 6, stroke: '#f59e0b', strokeWidth: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Signals Table */}
            {filtered.length > 0 ? (
              <div className="overflow-x-auto">
                <div className="grid grid-cols-12 px-5 py-3 bg-slate-800/50 border-b border-slate-700 table-header text-[10px] uppercase tracking-wider">
                  <div className="col-span-2">Ticker</div>
                  <div>Source</div>
                  <div>Signal</div>
                  <div className="text-right">Entrée</div>
                  <div className="text-right">Actuel</div>
                  <div className="text-right">Target</div>
                  <div className="text-right">Stop</div>
                  <div className="text-right">P&L</div>
                  <div className="text-center">Status</div>
                  <div className="col-span-2">Date</div>
                </div>
                {filtered.slice(0, 50).map((sig, i) => {
                  const signalColors = {
                    'JOUER_AVANT': 'text-emerald-400 bg-emerald-400/10',
                    'SWING_POST': 'text-blue-400 bg-blue-400/10',
                    'BUY': 'text-emerald-400 bg-emerald-400/10',
                    'STRONG_BUY': 'text-emerald-300 bg-emerald-300/10',
                    'ATTENDRE': 'text-amber-400 bg-amber-400/10',
                    'SELL': 'text-red-400 bg-red-400/10',
                  };
                  const statusIcons = {
                    'WIN': <CheckCircle className="w-4 h-4 text-emerald-400" />,
                    'LOSS': <XCircle className="w-4 h-4 text-red-400" />,
                    'OPEN': <Clock className="w-4 h-4 text-blue-400" />,
                  };
                  const pnl = sig.pnl_pct;
                  const signalStyle = signalColors[sig.signal] || 'text-slate-400 bg-slate-400/10';
                  return (
                    <div key={i} className={`grid grid-cols-12 px-5 py-2.5 items-center text-sm ${i < filtered.length - 1 ? 'border-b border-slate-700/30' : ''} hover:bg-slate-800/30 transition-colors`}
                      data-testid={`track-signal-${i}`}>
                      <div className="col-span-2">
                        <span className="font-mono font-bold text-white">{sig.ticker}</span>
                      </div>
                      <div>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">{sig.signal_type}</span>
                      </div>
                      <div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${signalStyle}`}>{sig.signal}</span>
                      </div>
                      <div className="text-right font-mono text-slate-300">${sig.entry_price?.toFixed(2) || '--'}</div>
                      <div className="text-right font-mono text-white font-semibold">${sig.current_price?.toFixed(2) || '--'}</div>
                      <div className="text-right font-mono text-slate-400">${sig.target_price?.toFixed(2) || '--'}</div>
                      <div className="text-right font-mono text-slate-400">${sig.stop_price?.toFixed(2) || '--'}</div>
                      <div className={`text-right font-mono font-bold ${pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {pnl !== null && pnl !== undefined ? `${pnl >= 0 ? '+' : ''}${pnl}%` : '--'}
                      </div>
                      <div className="flex justify-center">{statusIcons[sig.status] || statusIcons['OPEN']}</div>
                      <div className="col-span-2 text-xs text-slate-500">
                        {sig.created_at ? new Date(sig.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '--'}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500" data-testid="track-record-empty">
                <Zap className="w-10 h-10 mb-3 text-slate-600" />
                <p className="text-sm font-medium">{sourceFilter === "ALL" ? "Aucun signal AI enregistré" : `Aucun signal ${sourceFilter}`}</p>
                <p className="text-xs text-slate-600 mt-1">Générez des signaux via les onglets Earnings ou Algo Trading</p>
              </div>
            )}
          </>
          );
        })()}
      </div>
    </div>
  );
}
